"use client";

import { useChat } from "@ai-sdk/react";
import { useSWRConfig } from "swr";
import ChatInput from "./chat-input";
import { useEffect } from "react";
import { unstable_serialize } from "swr/infinite";
import { Messages } from "./messages";
import { getChatHistoryPaginationKey } from "@/hooks/use-chat-history";
import { toast } from "sonner";
import { useAppContext } from "@/context/app";
import { ChevronRightIcon, LoaderIcon, TriangleAlertIcon } from "lucide-react";
import Link from "next/link";
import ChatHeader from "./chat-header";
import { cn } from "@/lib/utils";
import Sidebar from "./sidebar";
import { API_ROUTES } from "@/constants/routes";
export default function Chat({
  chatId,
  initialMessages,
  autoResume,
  initialAgentId
}) {
  const {
    mutate
  } = useSWRConfig();
  const {
    subscription,
    isSidebarOpen,
    toggleSidebarOpen
  } = useAppContext();
  const {
    messages,
    setMessages,
    handleSubmit,
    input,
    setInput,
    // append,
    status,
    // stop,
    reload,
    experimental_resume
  } = useChat({
    api: "/api/chat",
    id: chatId,
    initialMessages,
    experimental_throttle: 100,
    sendExtraMessageFields: true,
    generateId: () => crypto.randomUUID(),
    experimental_prepareRequestBody: body => ({
      id: chatId,
      agentId: initialAgentId,
      message: body.messages.at(-1)
    }),
    onFinish: () => {
      mutate(unstable_serialize((index, prev) => getChatHistoryPaginationKey(index, prev, initialAgentId)));
      mutate(API_ROUTES.SUBSCRIPTION);
    },
    onError: error => {
      toast.error(error.message);
    }
  });
  useEffect(() => {
    if (autoResume) {
      experimental_resume();
    }

    // note: this hook has no dependencies since it only needs to run once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (subscription.isLoading) {
    return <div className="flex w-full h-dvh justify-center items-center">
        <LoaderIcon className="animate-spin" />
      </div>;
  }
  if (!subscription.data?.remaining_token) {
    return <div className="flex w-full h-dvh fixed z-50 bg-white inset-0 justify-center items-center flex-col gap-6">
        <div className="bg-neutral-200 size-16 flex justify-center items-center rounded-xl text-neutral-600">
          <TriangleAlertIcon className="size-8" />
        </div>

        <div className="flex flex-col gap-4 items-center max-w-sm text-center">
          <h1 className="text-2xl font-semibold">
            Your token usage has reached the limit
          </h1>
          <Link href="/app/pricing" className="bg-neutral-900 hover:bg-neutral-800 transition-colors text-white px-6 py-2 w-max font-semibold rounded-full">
            Upgrade plan
          </Link>
        </div>
      </div>;
  }
  return <div className="grow h-full flex flex-col relative">
      <ChatHeader selectedAgentId={initialAgentId} />

      <button type="button" className="fixed top-25 left-2 cursor-pointer z-30" onClick={toggleSidebarOpen}>
        <ChevronRightIcon className={cn("transition-transform text-foreground/65 hover:text-foreground", isSidebarOpen ? "rotate-180" : "")} />
      </button>

      <div className="flex size-full">
        {isSidebarOpen && <Sidebar agentId={initialAgentId} />}

        <div className="flex w-full h-[calc(100dvh-83px)] mt-auto justify-center relative items-center flex-col">
          <div className=" max-w-2xl mx-auto w-full h-16 absolute top-0 z-10 bg-[linear-gradient(0deg,rgba(245,244,241,0.09)_0%,#F5F4F1_93.08%)]"></div>
          <Messages chatId={chatId} status={status} messages={messages} setMessages={setMessages} reload={reload} />

          <div className="w-full flex justify-center flex-col gap-6 items-center pb-4 pt-4  overflow-y-scroll  [&::-webkit-scrollbar]:invisible">
            <ChatInput chatId={chatId} handleSubmit={handleSubmit} input={input} setInput={setInput} status={status} />

            <p className="text-center opacity-50">AI can make mistakes.</p>
          </div>
        </div>
      </div>
    </div>;
}